import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Mail, CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationPending = () => {
  const location = useLocation();
  const email = location.state?.email || '';

  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendCount, setResendCount] = useState(0);

  const handleResendEmail = async () => {
    setResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      const response = await fetch(`${BACKEND_URL}/api/public/member-registration/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.status === 429) {
        setResendError('Maximum resend attempts reached (3). Please contact the studio if you need assistance.');
        return;
      }

      if (response.status === 404) {
        setResendError('Registration not found. Please try registering again.');
        return;
      }

      if (!response.ok) {
        setResendError(data.detail || 'Failed to resend verification email.');
        return;
      }

      setResendSuccess(true);
      setResendCount(prev => prev + 1);

    } catch (err) {
      console.error('Resend error:', err);
      setResendError('An error occurred. Please try again or contact the studio at (352) 428-0086.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Check Your Email!</CardTitle>
            <CardDescription>
              We've sent a verification link to <strong>{email}</strong>
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Success notification for resend */}
            {resendSuccess && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Verification email sent successfully! Please check your inbox.
                </AlertDescription>
              </Alert>
            )}

            {/* Error notification */}
            {resendError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{resendError}</AlertDescription>
              </Alert>
            )}

            {/* What happens next */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">What happens next?</h3>

              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-semibold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Check your email</p>
                    <p className="text-sm text-gray-600">
                      Look for an email from Owaza Martial Arts with the subject "Verify Your Email Address"
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-semibold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Click the verification link</p>
                    <p className="text-sm text-gray-600">
                      The link is valid for 24 hours. Click it to verify your email address.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-semibold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Admin review</p>
                    <p className="text-sm text-gray-600">
                      Once verified, our team will review your registration (usually within 24-48 hours)
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Welcome email</p>
                    <p className="text-sm text-gray-600">
                      You'll receive a welcome email with login instructions for the member portal
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Resend button */}
            <div className="border-t pt-6">
              <p className="text-sm text-gray-600 mb-4">
                Didn't receive the email? Check your spam folder or request a new one.
              </p>
              <Button
                onClick={handleResendEmail}
                disabled={resending || resendCount >= 3}
                variant="outline"
                className="w-full"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    {resendCount >= 3 ? 'Maximum Attempts Reached' : 'Resend Verification Email'}
                  </>
                )}
              </Button>
              {resendCount > 0 && resendCount < 3 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {resendCount} of 3 resend attempts used
                </p>
              )}
            </div>

            {/* Help */}
            <Alert className="bg-amber-50 border-amber-200">
              <Clock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                Need help? Contact us at <a href="tel:+13524280086" className="underline font-medium">(352) 428-0086</a> or{' '}
                <a href="mailto:info@owazamartialarts.com" className="underline font-medium">info@owazamartialarts.com</a>
              </AlertDescription>
            </Alert>

            {/* Back to home */}
            <div className="text-center pt-4">
              <Link to="/" className="text-sm text-red-600 hover:text-red-700 underline">
                Return to Homepage
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerificationPending;
